import { useEffect, useState } from 'react'
import { Check, Zap, X } from 'lucide-react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { stripeApi } from '../../lib/api'
import config from '../../config'
import useAuthStore from '../../store/authStore'
import useOrgStore from '../../store/orgStore'
import { getPlanRank, getSubscriptionPlan } from '../../lib/subscription'
import toast from 'react-hot-toast'

export function UpgradeModal({ isOpen, onClose, trigger }) {
  const { user } = useAuthStore()
  const { org, usage } = useOrgStore()
  const [loading, setLoading] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState('')

  const plans = ['starter', 'pro', 'business']
  const currentPlan = getSubscriptionPlan({ user, org, usage })
  const currentPlanRank = getPlanRank(currentPlan)
  const firstUpgradeablePlan =
    plans.find((planKey) => getPlanRank(planKey) > currentPlanRank) || ''
  const selectedPlanRank = getPlanRank(selectedPlan)
  const isUpgradeSelection = Boolean(selectedPlan) && selectedPlanRank > currentPlanRank
  const selectedPlanConfig = selectedPlan ? config.PLANS[selectedPlan] : null

  useEffect(() => {
    if (isOpen) {
      setSelectedPlan(firstUpgradeablePlan)
    }
  }, [firstUpgradeablePlan, isOpen])

  async function handleUpgrade() {
    if (!selectedPlanConfig || !isUpgradeSelection) return

    setLoading(true)
    try {
      const data = await stripeApi.createCheckout(selectedPlan)
      const checkoutUrl = data.checkoutUrl || data.redirectUrl || data.url

      if (checkoutUrl) {
        window.location.href = checkoutUrl
      } else {
        toast.error('Could not create checkout session. Please try again.')
        setLoading(false)
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong')
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upgrade your plan"
      subtitle={trigger || 'Unlock more jobs, CVs and features'}
      size="lg"
    >
      <div className="p-6">
        {/* Plan selector */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {plans.map((planKey) => {
            const plan = config.PLANS[planKey]
            const isSelected = selectedPlan === planKey
            const isPopular = planKey === 'pro'
            const planRank = getPlanRank(planKey)
            const isCurrentPlan = planKey === currentPlan
            const isDowngrade = planRank < currentPlanRank
            const isSelectable = !isCurrentPlan && !isDowngrade

            return (
              <div
                key={planKey}
                onClick={isSelectable ? () => setSelectedPlan(planKey) : undefined}
                className={`relative rounded-2xl border-2 p-4 transition-all duration-200 ${
                  isSelected
                    ? 'border-brand-600 bg-brand-50'
                    : isSelectable
                      ? 'border-gray-200 hover:border-gray-300 bg-white cursor-pointer'
                      : 'border-gray-200 bg-gray-50 opacity-70 cursor-not-allowed'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-brand-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-gray-900">{plan.name}</p>
                    {isCurrentPlan && (
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Current plan
                      </span>
                    )}
                    {isDowngrade && (
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Locked
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span
                      className={`text-2xl font-bold ${
                        isSelected ? 'text-brand-700' : 'text-gray-800'
                      }`}
                    >
                      {plan.price}
                    </span>
                    <span className="text-xs text-gray-500">/mo</span>
                  </div>
                </div>

                <ul className="space-y-1.5">
                  {plan.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-1.5 text-xs text-gray-600"
                    >
                      <Check
                        className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${
                          isSelected ? 'text-brand-600' : 'text-green-500'
                        }`}
                      />
                      {feature}
                    </li>
                  ))}
                </ul>

                {isSelected && (
                  <div className="absolute top-3 right-3">
                    <div className="w-5 h-5 rounded-full bg-brand-600 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  </div>
                )}

                {isCurrentPlan && (
                  <p className="mt-3 text-xs text-gray-500">
                    You are already subscribed to this plan.
                  </p>
                )}
                {isDowngrade && (
                  <p className="mt-3 text-xs text-gray-500">
                    Downgrades are not available yet. Cancel and resubscribe after your billing period ends.
                  </p>
                )}
              </div>
            )
          })}
        </div>

        {/* CTA */}
        <Button
          variant="primary"
          fullWidth
          size="lg"
          loading={loading}
          disabled={!isUpgradeSelection || !selectedPlanConfig}
          onClick={handleUpgrade}
          icon={<Zap className="w-4 h-4" />}
        >
          {!selectedPlanConfig
            ? 'Select an upgrade plan'
            : !isUpgradeSelection
              ? 'Downgrades are not available yet'
              : `Upgrade to ${selectedPlanConfig?.name} for ${selectedPlanConfig?.price}/mo`}
        </Button>

        <p className="text-xs text-center text-gray-400 mt-3">
          Secured by Stripe · Cancel anytime · No hidden fees
        </p>
        <p className="text-xs text-center text-gray-400 mt-1">
          After payment, you will return to an in-app confirmation screen instead of being sent back to the landing page.
        </p>
      </div>
    </Modal>
  )
}

export function UpgradeBanner({ message, onUpgrade }) {
  const [dismissed, setDismissed] = useState(false)

  if (dismissed) return null

  return (
    <div className="bg-gradient-to-r from-brand-50 to-blue-50 border border-brand-200 rounded-2xl p-4 flex items-center justify-between gap-4 mb-6">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl bg-brand-100 flex items-center justify-center shrink-0">
          <Zap className="w-4 h-4 text-brand-600" />
        </div>
        <p className="text-sm text-brand-800 font-medium">{message}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="primary" size="sm" onClick={onUpgrade}>
          Upgrade
        </Button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 text-gray-400 hover:text-gray-600"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
